import { Badge, Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import type { DeploymentRecord } from '@/services/deploymentService';
import type { ProgressSnapshot } from '@/utils/deployProgressPoller';
import { imageDisplay, isLive, shortUser, type ImagesByChannel } from '@/utils/deployStatus';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { RunMoreMenu, type RunMenuHandlers } from './RunMoreMenu';
import { RunProgress } from './RunProgress';
import { StatusPill } from './StatusPill';
import './deployments.css';

interface LiveRunCardProps extends RunMenuHandlers {
  record: DeploymentRecord;
  snapshot?: ProgressSnapshot;
  images?: ImagesByChannel;
  /** Leave out the Logs button on the log page itself. */
  hideLogsLink?: boolean;
}

/** One running deploy: who and what on top, the live status line under it. */
export function LiveRunCard({ record, snapshot, images, hideLogsLink = false, ...handlers }: LiveRunCardProps) {
  const image = imageDisplay(record, images);
  return (
    <Card className={`deploy-live-card${isLive(record) ? ' deploy-live-card--live' : ''}`}>
      <div className="deploy-live-card__head">
        <div className="deploy-live-card__who">
          <span className="deploy-live-card__client" title={record.clientName}>
            {record.clientName}
          </span>
          <StatusPill status={record.status} attemptNumber={record.attemptNumber} />
          {record.mode === 'plan' && <Badge bg="secondary">Plan only</Badge>}
        </div>
        <div className="deploy-live-card__actions">
          {!hideLogsLink && (
            <Link
              className="btn btn-sm btn-outline-secondary"
              to={`/deployments/${encodeURIComponent(record.deploymentId)}/logs`}
            >
              Logs
            </Link>
          )}
          {handlers.onStop && (
            <Button size="sm" variant="outline-danger" onClick={() => handlers.onStop?.(record)}>
              Stop
            </Button>
          )}
          <RunMoreMenu record={record} {...handlers} hideStop />
        </div>
      </div>
      <div className="deploy-live-card__meta">
        {/* A long image name or label is cut to one line. The whole text shows on hover. */}
        <span
          className={image.named ? 'deploy-live-card__long' : 'deploy-live-card__long mono'}
          title={image.named ? `${image.label} (${image.tag})` : image.tag}
        >
          {image.label}
        </span>
        {image.dev && <Badge bg="warning">Dev</Badge>}
        {record.deploymentLabel && (
          <span className="deploy-live-card__long" title={record.deploymentLabel}>
            {record.deploymentLabel}
          </span>
        )}
        <span title={record.initiatedBy}>by {shortUser(record.initiatedBy) || 'unknown'}</span>
        <span title={formatNzDateTime(record.startedAt)}>started {timeAgo(record.startedAt)}</span>
      </div>
      <RunProgress record={record} snapshot={snapshot} />
    </Card>
  );
}

export default LiveRunCard;
